/**
 * Activity's Tasks — `docs/activity.md` §8.0–§8.6 (Boards 9, 16 C, 19).
 *
 * The rail on the left (desktop and iPad), and either **List** — the list
 * engine, unchanged — or **Board**, a column per person or, with a task list
 * open, a column per bucket. *List · Board* sits in the page head, before
 * the people button (Board 9 A). A phone has no rail: its page bar's view
 * button, beside the people button, is one control for both — the *List ·
 * Board* toggle over the rail's rows, in one sheet (Aaron, Oct 27; it was a
 * *List · Board* button and a *Tasks for* line above the list).
 *
 * Deep links (§13.3): `?tab=tasks&task=<id>` opens the Task sheet;
 * `&list=<id>&view=board` opens a list on the Board. A link changes what
 * this visit shows and never what is remembered.
 *
 * Each task list, and the people's tasks, open in the view they were last in
 * — List or Board — on this device (Aaron, Oct 23): `tasks.views`, keyed by
 * the list's id or `people`, in the remembered state (§4.6). One never seen
 * opens in the last one used anywhere.
 */
import { useCallback, useEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import { listViewOf } from '@rowted/shared'
import { useSearchParams } from 'react-router-dom'
import { cn } from '../../lib/cn.js'
import { usePhone } from '../../lib/use-narrow.js'
import { activityRequest } from '../api.js'
import { useActivity } from '../activity-context.js'
import { Glyph } from '../glyphs.js'
import { TaskBoard } from './task-board.js'
import { TaskListView } from './task-list.js'
import { TaskListSheet } from './task-list-sheet.js'
import { PhoneTasksView, TaskRail } from './task-rail.js'
import { openTaskSheet, TaskSheetHost, TASKS_CHANGED_EVENT } from './task-sheet.js'
import { openTaskChatNext } from './task-chat.js'
import type { TaskList } from './types.js'

type View = 'list' | 'board'

/** *List · Board* (Board 9 A): 30 px segments in a 3 px track. */
function ListBoardSwitch({ view, onChange }: { view: View; onChange: (view: View) => void }) {
  const option = (value: View, label: string, glyph: 'menu' | 'board') => (
    <button key={value} type="button" role="radio" aria-checked={view === value} onClick={() => onChange(value)}
      className={cn('inline-flex h-[30px] items-center gap-1.5 whitespace-nowrap rounded-lg px-3 text-[12.5px] font-semibold',
        view === value ? 'bg-surface text-text shadow-[0_1px_2px_rgba(20,20,10,.12)]' : 'text-text-muted hover:text-text')}>
      <Glyph name={glyph} size={14} />{label}
    </button>
  )
  return (
    <span role="radiogroup" aria-label="List or Board" className="inline-flex shrink-0 rounded-[10px] bg-line-soft p-[3px]">
      {option('list', 'List', 'menu')}
      {option('board', 'Board', 'board')}
    </span>
  )
}

export function TasksTab() {
  const { state, change } = useActivity()
  const phone = usePhone()
  const [params, setParams] = useSearchParams()

  /* What a link asks for this visit — read once, then the link's own words leave the address. */
  const [linked, setLinked] = useState<{ listId?: string; view?: View }>(() => ({
    ...(params.get('list') ? { listId: params.get('list')! } : {}),
    ...(params.get('view') === 'board' || params.get('view') === 'list' ? { view: params.get('view') as View } : {}),
  }))
  useEffect(() => {
    const task = params.get('task')
    /* `&chat=1` — the bell's link for a chat message — opens it on the chat (note 1.6). */
    if (task && params.get('chat') === '1') openTaskChatNext()
    if (task) openTaskSheet(task)
    if (params.get('view') === 'board' || params.get('view') === 'list') {
      /* `view` is also the list engine's own parameter; a word in it would read as a broken view. */
      setParams((current) => { const next = new URLSearchParams(current); next.delete('view'); return next }, { replace: true })
    }
    // Once, on arrival.
  }, [])

  const [lists, setLists] = useState<TaskList[] | null>(null)
  const loadLists = useCallback(() => {
    activityRequest<{ items: TaskList[] }>('/task-lists')
      .then((answer) => setLists(answer.items))
      .catch(() => setLists([]))
  }, [])
  useEffect(() => {
    loadLists()
    window.addEventListener(TASKS_CHANGED_EVENT, loadLists)
    return () => window.removeEventListener(TASKS_CHANGED_EVENT, loadLists)
  }, [loadLists])

  const wantedList = linked.listId ?? state.tasks.listId ?? null
  const list = wantedList && lists ? lists.find((l) => l.id === wantedList) ?? null : null
  const listId = lists === null ? wantedList : list?.id ?? null
  const viewKey = listId ?? 'people'
  const view: View = linked.view ?? listViewOf(state.tasks.views, viewKey) ?? state.tasks.view

  const pickList = (id: string | null) => {
    if (id === listId && !linked.listId) return
    setLinked({})
    change({ tasks: { ...state.tasks, listId: id } })
    setParams((current) => {
      const next = new URLSearchParams(current)
      next.delete('list')
      next.delete('saved')
      next.delete('view')
      return next
    }, { replace: true })
  }
  const pickView = (next: View) => {
    setLinked((current) => ({ ...current, view: undefined }))
    change({ tasks: { ...state.tasks, view: next, views: [{ key: viewKey, view: next }] } })
  }
  /* The phone's sheet: a list picked after its toggle was touched opens in the toggle's view (Aaron, Oct 27). */
  const pickListAs = (id: string | null, as?: View) => {
    /* The same row: the toggle has already shown its view there. */
    if (!as || (id === listId && !linked.listId)) { pickList(id); return }
    setLinked({})
    change({ tasks: { ...state.tasks, listId: id, view: as, views: [{ key: id ?? 'people', view: as }] } })
    setParams((current) => {
      const next = new URLSearchParams(current)
      next.delete('list')
      next.delete('saved')
      next.delete('view')
      return next
    }, { replace: true })
  }

  const [sheet, setSheet] = useState<{ list: TaskList | null } | null>(null)

  /*
   * *List · Board* goes in the page head, before the people button (Board 9 A, 19 A) — on a phone
   * as the page bar's view button, which also picks whose tasks or which list (Aaron, Oct 27). The
   * head is drawn again when the width crosses a phone's, so the slot is looked for again then.
   */
  const [slot, setSlot] = useState<HTMLElement | null>(null)
  useEffect(() => { setSlot(document.getElementById('activity-head-slot')) }, [phone])

  return (
    <div className="flex min-h-0 flex-1 items-stretch">
      {slot && createPortal(phone
        ? (
          <PhoneTasksView lists={lists ?? []} listId={listId} view={view} onView={pickView} onPick={pickListAs}
            onNew={() => setSheet({ list: null })} />
        )
        : <ListBoardSwitch view={view} onChange={pickView} />, slot)}
      {!phone && (
        <TaskRail lists={lists ?? []} listId={listId} onPick={pickList} onNew={() => setSheet({ list: null })} />
      )}
      <div className={cn('flex min-w-0 flex-1 flex-col', phone ? 'gap-0' : 'gap-3 p-4')}>
        <div className={cn('flex min-w-0 flex-1 flex-col', phone && 'px-3.5 pt-2.5')}>
          {view === 'board'
            ? <TaskBoard key={listId ?? 'people'} list={list} onEditList={() => setSheet({ list })} />
            : <TaskListView key={listId ?? 'people'} listId={listId} />}
        </div>
      </div>
      <TaskListSheet
        open={sheet !== null}
        list={sheet?.list ?? null}
        onClose={() => setSheet(null)}
        onSaved={(saved) => { loadLists(); if (!sheet?.list) pickList(saved.id) }}
        onDeleted={() => { loadLists(); pickList(null) }}
      />
      {/* `?task=` and `?chat=` are the sheet's own: it takes them out as it closes, back first (Aaron, Oct 26). */}
      <TaskSheetHost />
    </div>
  )
}
